import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, Loader2, Eye, EyeOff, CheckCircle2, ShieldCheck } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import API_URL from '../lib/apiUrl';

const formatApiErrorDetail = (detail) => {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
};

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) return setError('Password must be at least 8 characters long');
    if (!/[A-Za-z]/.test(password) || !/\d/.test(password))
      return setError('Password must contain at least one letter and one number');
    if (password !== confirmPassword) return setError('Passwords do not match');

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, { token, password });
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || 'Could not reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4 py-12"
        style={{ background: 'linear-gradient(180deg, #060914 0%, #0a0f1f 100%)' }}
      >
        <div className="text-center max-w-md p-8">
          <h1 className="font-heading font-bold text-2xl text-white mb-3">Invalid reset link</h1>
          <p className="text-slate-400 text-sm mb-6">
            This password reset link is missing a token. Please request a new link.
          </p>
          <Button
            onClick={() => navigate('/forgot-password')}
            className="bg-white hover:bg-slate-100 text-slate-900 px-6 py-3 font-semibold rounded-lg"
            data-testid="reset-request-new-btn"
          >
            Request a new link
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-12 relative overflow-hidden"
      style={{
        background:
          'radial-gradient(ellipse at top, rgba(52,211,153,0.08), transparent 60%), linear-gradient(180deg, #060914 0%, #0a0f1f 100%)',
      }}
    >
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
          backgroundSize: '64px 64px',
        }}
      />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="relative w-full max-w-md"
      >
        <div
          className="rounded-2xl p-8 md:p-10"
          style={{
            background: 'linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.02))',
            border: '1px solid rgba(255,255,255,0.08)',
            backdropFilter: 'blur(16px)',
            boxShadow: '0 25px 60px -10px rgba(0,0,0,0.5)',
          }}
        >
          <div className="text-center mb-7">
            <div
              className="inline-flex items-center justify-center w-12 h-12 rounded-xl mb-4 border"
              style={{ background: 'rgba(52,211,153,0.08)', borderColor: 'rgba(52,211,153,0.3)' }}
            >
              {done ? <CheckCircle2 className="h-6 w-6 text-emerald-300" /> : <ShieldCheck className="h-6 w-6 text-emerald-300" />}
            </div>
            <h1 className="font-heading font-bold text-2xl text-white mb-2">
              {done ? 'Password reset!' : 'Choose a new password'}
            </h1>
            <p className="text-slate-400 text-sm">
              {done
                ? 'Your password has been updated. You can now sign in.'
                : 'Pick something strong — minimum 8 characters with a letter and a number.'}
            </p>
          </div>

          {!done ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div
                  className="px-4 py-3 rounded-lg text-sm text-red-300"
                  style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)' }}
                  data-testid="reset-error"
                >
                  {error}
                </div>
              )}
              <PwField
                value={password}
                onChange={setPassword}
                show={showPassword}
                onToggleShow={() => setShowPassword(!showPassword)}
                label="New Password"
                autoFocus
                testid="reset-password-input"
              />
              <PwField
                value={confirmPassword}
                onChange={setConfirmPassword}
                show={showPassword}
                onToggleShow={() => setShowPassword(!showPassword)}
                label="Confirm Password"
                testid="reset-confirm-input"
              />
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-white hover:bg-slate-100 text-slate-900 py-6 font-semibold rounded-lg"
                data-testid="reset-submit-btn"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Reset Password'}
              </Button>
            </form>
          ) : (
            <Button
              onClick={() => navigate('/auth')}
              className="w-full bg-white hover:bg-slate-100 text-slate-900 py-6 font-semibold rounded-lg"
              data-testid="reset-back-to-signin-btn"
            >
              Sign In
            </Button>
          )}
        </div>
      </motion.div>
    </div>
  );
};

const PwField = ({ value, onChange, show, onToggleShow, label, autoFocus, testid }) => (
  <div>
    <label className="block font-mono text-[10px] uppercase tracking-widest text-slate-400 mb-1.5">{label}</label>
    <div className="relative">
      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
      <input
        type={show ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required
        autoFocus={autoFocus}
        className="w-full pl-10 pr-10 py-3 bg-white/5 border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-emerald-400/60 focus:ring-1 focus:ring-emerald-400/40 transition-colors"
        data-testid={testid}
      />
      <button
        type="button"
        onClick={onToggleShow}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
        aria-label="Toggle password visibility"
      >
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  </div>
);

export default ResetPassword;
